import axios from '@utils/axiosExt';
import Cookies from 'js-cookie';
import { encodeWithKeyAndShift, decodeWithKeyAndShift } from './base64Utils';
import endpoints from '../config/endpoints';
import { errorLogger } from './applicationLogUtils';

const MAX_RETRIES = 3;
// Exponential backoff: 2s after attempt 1, 4s after attempt 2
const BASE_RETRY_DELAY_MS = 2000;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export interface RefreshResult {
    success: boolean;
    data?: any;
    error?: any;
}

export const performRefreshWithRetry = async (): Promise<RefreshResult> => {
    const isBeneficiaryLogged = localStorage.getItem('isBeneficiaryLogged') || 'false';
    const refreshToken = Cookies.get('refresh-token');

    if (!refreshToken) {
        errorLogger('[Token Refresh] No refresh token found in cookies');
        return { success: false, error: new Error('No refresh token available') };
    }

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        console.log(`[Token Refresh] Attempt ${attempt}/${MAX_RETRIES} — calling refresh endpoint...`);
        try {
            const encodedData = encodeWithKeyAndShift(JSON.stringify({ refreshToken }));
            const res = await axios.post(
                isBeneficiaryLogged === 'true' ? endpoints.beneficiaryRefreshToken : endpoints.refreshTokken,
                { data: encodedData }
            );
            const decodedData = JSON.parse(decodeWithKeyAndShift(res?.data?.data?.data));

            if (decodedData[0]?.auth_status === 'SUCCESS') {
                console.log(`[Token Refresh] ✓ Attempt ${attempt}/${MAX_RETRIES} succeeded`);
                return { success: true, data: decodedData[0] };
            }

            const status = decodedData[0]?.auth_status;
            if (attempt < MAX_RETRIES) {
                const delay = BASE_RETRY_DELAY_MS * attempt;
                console.warn(`[Token Refresh] ✗ Attempt ${attempt}/${MAX_RETRIES} non-SUCCESS (${status}). Retrying in ${delay / 1000}s...`);
                await sleep(delay);
            } else {
                errorLogger(`[Token Refresh] ✗ Attempt ${attempt}/${MAX_RETRIES} non-SUCCESS (${status}). No more retries.`);
            }
        } catch (error: any) {
            const msg = error?.response?.data || error?.response || error?.message || error;
            if (attempt < MAX_RETRIES) {
                const delay = BASE_RETRY_DELAY_MS * attempt;
                console.warn(`[Token Refresh] ✗ Attempt ${attempt}/${MAX_RETRIES} error: ${msg}. Retrying in ${delay / 1000}s...`);
                await sleep(delay);
            } else {
                errorLogger(`[Token Refresh] ✗ Attempt ${attempt}/${MAX_RETRIES} error: ${msg}. No more retries.`);
            }
        }
    }

    console.error(`[Token Refresh] ✗ All ${MAX_RETRIES} attempts exhausted. Session cannot be renewed.`);
    return { success: false, error: new Error(`Token refresh failed after ${MAX_RETRIES} attempts`) };
};
